import { useRef } from 'react';

import { useImpression, useTracker } from '@archipro-website/tracker';
import { reportingDevice } from '~/modules/tracking/util/reporting-device';
import type { GetMatchedEntryPoint } from '~/state/ui-state-get-matched-modal';

/**
 * Reports a Get Matched prompt as seen, so its opens have a denominator.
 *
 * Attach the returned ref to the element carrying the control, not to the
 * surrounding module. A prompt whose control is not rendered was not offered,
 * and an impression for it is a denominator nothing can ever convert against —
 * the browse lead drops its rail below `md`, so anchoring to the module gave it
 * a mobile open rate of zero by construction.
 *
 * Without this a click on a prompt is an absolute number, and the prompt shown
 * at every second break in a long grid outscores one shown once at the top of
 * the page purely by being shown more. Only a seen-count separates a prompt
 * that works from one that is simply frequent.
 *
 * Seen is the MRC definition the result tiles already use — 50% of the element
 * visible for a continuous second — via {@link useImpression}. Deliberately not
 * `useLogImpression`, which wraps the same hook but hard-codes the `Impression`
 * event, returns early unless an item id and a professional id are both
 * positive, and reports under a name the V2 pipeline drops. A prompt is not a
 * library item and has neither id, so that wrapper would report nothing twice
 * over.
 *
 * One firing per mounted prompt. The grid keys its injected modules by cycle
 * index, so each appearance is its own component and reports itself once —
 * which is what "counted each time it genuinely appears" needs. Changing the
 * display column count does not remount them, so it produces no false repeats.
 */
export const useGetMatchedPromptImpression = <
    TElement extends Element = HTMLDivElement,
>(
    entryPoint: GetMatchedEntryPoint,
    /**
     * Whether the prompt is on the page at all. A host that renders its
     * container unconditionally and the prompt only sometimes passes this, so
     * the container being seen is not mistaken for the prompt being seen.
     */
    enabled = true,
) => {
    const ref = useRef<TElement | null>(null);
    const tracker = useTracker();

    useImpression({
        ref,
        disable: !enabled,
        onImpression: async (url: URL) => {
            const payload = { entryPoint, device: reportingDevice() };

            await tracker.log('GetMatchedPromptImpression', {
                url,
                data: {
                    ...payload,
                    // The entry point is the series key here, because the
                    // question this event answers is which prompt was seen. On
                    // the questionnaire's own events `Source` is the flow, and
                    // the entry point rides in `ExtraData` instead.
                    Source: entryPoint,
                    ExtraData: JSON.stringify(payload),
                },
            });
        },
    });

    return ref;
};
